/**
 * vCard Export Utility
 * Generates vCard (.vcf) format from profile data
 */

/**
 * Generate vCard string from profile data
 * @param {Object} profile - Profile object
 * @param {Object} options - Additional options
 * @returns {string} vCard formatted string
 */
export const generateVCard = (profile, options = {}) => {
  const {
    includeAvatar = false,
    includeSocial = true,
    includeWallet = true,
  } = options;

  const lines = [];
  
  // vCard header
  lines.push('BEGIN:VCARD');
  lines.push('VERSION:3.0');
  
  // Name
  if (profile.display_name) {
    // FN = Full Name
    lines.push(`FN:${escapeVCardValue(profile.display_name)}`);
    // N = Name (Family;Given;Additional;Prefix;Suffix)
    const nameParts = profile.display_name.split(' ');
    if (nameParts.length >= 2) {
      lines.push(`N:${nameParts.slice(1).join(' ')};${nameParts[0]};;;`);
    } else {
      lines.push(`N:;${profile.display_name};;;`);
    }
  } else if (profile.username) {
    lines.push(`FN:${escapeVCardValue(profile.username)}`);
    lines.push(`N:;${profile.username};;;`);
  }
  
  // Username/Nickname
  if (profile.username) {
    lines.push(`NICKNAME:${escapeVCardValue(profile.username)}`);
  }
  
  // Bio/Note
  if (profile.bio) {
    lines.push(`NOTE:${escapeVCardValue(profile.bio)}`);
  }
  
  // URL - HubID Profile
  if (profile.username) {
    const profileUrl = `${window.location.origin}/#/u/${profile.username}`;
    lines.push(`URL:${profileUrl}`);
  }
  
  // Wallet Address (as custom field)
  if (includeWallet && profile.wallet_address) {
    lines.push(`X-WALLET-ADDRESS:${profile.wallet_address}`);
    lines.push(`X-WALLET-NETWORK:ethereum`);
  }
  
  // Avatar/Photo
  if (includeAvatar && profile.avatar) {
    // For vCard, we can include a URL to the photo
    lines.push(`PHOTO;VALUE=URI:${profile.avatar}`);
  }
  
  // Social Links (from blocks)
  if (includeSocial && profile.blocks) {
    profile.blocks.forEach((block) => {
      if (block.type === 'link' && block.data?.url) {
        const url = block.data.url;
        const title = block.data.title || 'Link';
        
        // Detect social media platforms
        if (url.includes('twitter.com') || url.includes('x.com')) {
          lines.push(`X-SOCIALPROFILE;TYPE=twitter:${url}`);
        } else if (url.includes('linkedin.com')) {
          lines.push(`X-SOCIALPROFILE;TYPE=linkedin:${url}`);
        } else if (url.includes('github.com')) {
          lines.push(`X-SOCIALPROFILE;TYPE=github:${url}`);
        } else if (url.includes('instagram.com')) {
          lines.push(`X-SOCIALPROFILE;TYPE=instagram:${url}`);
        } else {
          // Generic URL
          lines.push(`URL;TYPE=${escapeVCardValue(title)}:${url}`);
        }
      }
    });
  }
  
  // Organization (if available)
  if (profile.featured?.organization) {
    lines.push(`ORG:${escapeVCardValue(profile.featured.organization)}`);
  }
  
  // Title (if available)
  if (profile.featured?.title) {
    lines.push(`TITLE:${escapeVCardValue(profile.featured.title)}`);
  }
  
  // Timestamp
  lines.push(`REV:${new Date().toISOString()}`);
  
  // vCard footer
  lines.push('END:VCARD');
  
  return lines.join('\r\n');
};

/**
 * Escape special characters in vCard values
 * @param {string} value - Value to escape
 * @returns {string} Escaped value
 */
const escapeVCardValue = (value) => {
  if (!value) return '';
  
  return String(value)
    .replace(/\\/g, '\\\\')
    .replace(/,/g, '\\,')
    .replace(/;/g, '\\;')
    .replace(/\n/g, '\\n')
    .replace(/\r/g, '');
};

/**
 * Download vCard as .vcf file
 * @param {string} vcardContent - vCard content string
 * @param {string} filename - Filename (without extension)
 */
export const downloadVCard = (vcardContent, filename = 'hubid-contact') => {
  const blob = new Blob([vcardContent], { type: 'text/vcard;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filename}.vcf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Generate vCard QR Code data URL
 * @param {string} vcardContent - vCard content string
 * @returns {string} Data URL for QR code
 */
export const getVCardQRData = (vcardContent) => {
  // vCard content can be used directly in QR code
  return vcardContent;
};

